import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import type { DefaultSelectSingleProps } from '@/components/types';
import type { FlattenOptionData } from '@/types/antd';
import { type BaseSelectOption, BaseSelectSingle } from '@/components/BaseSelectSingle';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedValue } from '@/std/hooks/useDebouncedValue';
import type { Fornecedor } from '../types/Fornecedor';
import { fornecedorGetQueryOptions, type FornecedorGetParams } from '../api/apiFornecedorGet';
import { FornecedorOption } from './FornecedorOption';

export function FornecedorSelect({
    allowClear = true,
    autoFocus = false,
    className,
    disabled = false,
    fetchOnMount = false,
    id,
    initialLabel,
    params,
    placeholder,
    ref,
    value,
    onChange,
    onClear,
    onDeselect,
    onSearch,
    onSelect,
}: DefaultSelectSingleProps<number, Fornecedor, FornecedorGetParams>) {
    const [search, setSearch] = useState('');
    const [searchDebounced] = useDebouncedValue(search, DEBOUNCE_TIME);

    const hasSearch = typeof searchDebounced === 'string' && searchDebounced.length > 0;
    const searchUsingWhere = typeof value === 'number' && !hasSearch;
    const enabled = searchUsingWhere || hasSearch || fetchOnMount;

    const { data, isFetching } = useQuery({
        ...fornecedorGetQueryOptions({
            params: {
                ...params,
                orderby: 'pes_nome_razaosocial',
                pesquisar: searchDebounced,
                qtde_registros: 10,
                registro_inicial: 0,
                where: searchUsingWhere ? `for_idpk=${value}` : undefined,
            },
        }),
        enabled,
    });

    const options: BaseSelectOption<number, Fornecedor>[] = useMemo(() => {
        if (!data) {
            if (value) {
                return [
                    {
                        label: initialLabel,
                        obj: null,
                        value,
                    },
                ];
            }

            return [];
        }

        return data.registros.map((fornecedor) => ({
            label: fornecedor.pessoa.pes_nome_razaosocial,
            obj: fornecedor,
            value: fornecedor.for_idpk,
        }));
    }, [data, value, initialLabel]);

    const optionRender = (
        oriOption: FlattenOptionData<BaseSelectOption<number, Fornecedor>>,
    ): ReactNode => {
        const fornecedor = oriOption.data.obj;

        if (fornecedor) {
            return <FornecedorOption fornecedor={fornecedor} />;
        }

        return oriOption.label;
    };

    const handleSearch = (value: string): void => {
        setSearch(value);
        onSearch?.(value);
    };

    const handleClear = (): void => {
        setSearch('');
        onClear?.();
    };

    return (
        <BaseSelectSingle
            allowClear={allowClear}
            autoFocus={autoFocus}
            className={className}
            disabled={disabled}
            id={id}
            loading={isFetching}
            onChange={onChange}
            onClear={handleClear}
            onDeselect={onDeselect}
            onSelect={onSelect}
            optionRender={optionRender}
            options={options}
            placeholder={placeholder}
            ref={ref}
            showSearch={{ filterOption: false, onSearch: handleSearch }}
            value={value}
        />
    );
}
